import { Component, Input, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { UnAuthorizedException } from 'src/app/core/logic/exception';
import { Dietitian } from 'src/app/data/dietitian/domain/dietitian';
import { PatientGroup } from 'src/app/data/dietitian/domain/patient_group/patient_group';

import { TrackerManagerComponent } from '../../../../../components/tracker/tracker-manager/tracker-manager.component';
import { EditPatientGroupFacade } from './edit-patient-group-modal.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-edit-patient-group-modal',
  templateUrl: './edit-patient-group-modal.component.html',
  styleUrls: ['./edit-patient-group-modal.component.scss'],
  providers: [EditPatientGroupFacade],
})
export class EditPatientGroupModalComponent {
  @ViewChild('trackerManager') trackerManager: TrackerManagerComponent;
  @Input() patientGroup?: PatientGroup;
  form: FormGroup;
  accountStatusSubscription?: Subscription;
  currentDietitian?: Dietitian;
  isLoading = true;
  error?: string;
  maxTrackers = 10;

  constructor(
    private bsModalRef: BsModalRef,
    private facade: EditPatientGroupFacade,
    private router: Router,
    private toastr: ToastrService,
  ) {
    // do nothing
  }

  async ngOnInit(): Promise<void> {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.minLength(2)]),
    });

    this.accountStatusSubscription = this.facade.accountStatus$.subscribe(
      (state) => {
        this.currentDietitian = state.entity;
      },
    );

    if (this.patientGroup && this.patientGroup.id.toString() !== '_NEW_') {
      this.form.patchValue({
        name: this.patientGroup.name,
      });
      await this.runWithLoading(async () => {
        if (this.patientGroup) {
          this.patientGroup = await this.facade.getPatientGroup(
            this.patientGroup.id.toString(),
          );
          this.form.patchValue({
            name: this.patientGroup.name,
          });
        }
      });
    } else {
      this.isLoading = false;
    }
  }

  hideModal() {
    this.router
      .navigate([], {
        queryParams: {
          create: undefined,
        },
        queryParamsHandling: 'merge',
      })
      .then();
    this.bsModalRef.hide();
  }

  async runWithLoading(func: () => void) {
    this.isLoading = true;
    try {
      func.call(this);
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.error = error.message;
      }
    }
    this.isLoading = false;
  }

  async savePatientGroup() {
    await this.runWithLoading(async () => {
      if (!this.currentDietitian) throw new UnAuthorizedException();
      try {
        const insertedGroup = await this.facade.savePatientGroup(
          {
            activatedPatientCount:
              this.patientGroup?.activatedPatientCount ?? 0,
            archivedPatientCount: this.patientGroup?.archivedPatientCount ?? 0,
            encouragementCount: this.patientGroup?.encouragementCount ?? 0,
            patientCount: this.patientGroup?.patientCount ?? 0,
            name: this.form.get('name')?.value,
            dietitianId: this.currentDietitian.dietitianId,
          },
          this.patientGroup && this.patientGroup.id.toString() !== '_NEW_'
            ? this.patientGroup.id.toString()
            : undefined,
        );
        await this.trackerManager.saveAllTrackers(insertedGroup);
        this.hideModal();
      } catch (e: any) {
        this.toastr.error(e.message);
      }
    });
  }

  get nameInvalid(): boolean | undefined {
    return (
      !this.form.get('name')?.valid &&
      (this.form.get('name')?.touched || this.form.get('name')?.dirty)
    );
  }

  ngOnDestroy(): void {
    this.accountStatusSubscription?.unsubscribe();
  }
}
